import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Mail, Phone, Calendar, CheckCircle, Circle } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function AdminMessages() {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMessage, setSelectedMessage] = useState(null);

    useEffect(() => {
        fetchMessages();
    }, []);

    const fetchMessages = async () => {
        try {
            const { data, error } = await supabase
                .from('contact_messages')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setMessages(data || []);
        } catch (error) {
            console.error('Error fetching messages:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleMarkAsRead = async (id, currentStatus) => {
        try {
            const { error } = await supabase
                .from('contact_messages')
                .update({ is_read: !currentStatus })
                .eq('id', id);

            if (error) throw error;

            // Update local state
            setMessages(messages.map(msg =>
                msg.id === id ? { ...msg, is_read: !currentStatus } : msg
            ));

            if (selectedMessage && selectedMessage.id === id) {
                setSelectedMessage({ ...selectedMessage, is_read: !currentStatus });
            }
        } catch (error) {
            console.error('Error updating message status:', error);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="h-[calc(100vh-100px)] flex flex-col md:flex-row gap-6">
            {/* Message List */}
            <div className={`flex-1 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col ${selectedMessage ? 'hidden md:flex' : 'flex'}`}>
                <div className="p-4 border-b border-gray-100">
                    <h1 className="text-xl font-bold text-gray-900">Messages</h1>
                    <p className="text-sm text-gray-500">{messages.filter(m => !m.is_read).length} non lus</p>
                </div>
                <div className="overflow-y-auto flex-1">
                    {messages.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                            Aucun message pour le moment.
                        </div>
                    ) : (
                        <div className="divide-y divide-gray-100">
                            {messages.map((msg) => (
                                <div
                                    key={msg.id}
                                    onClick={() => setSelectedMessage(msg)}
                                    className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${selectedMessage?.id === msg.id ? 'bg-primary-50' : ''} ${!msg.is_read ? 'bg-blue-50/50' : ''}`}
                                >
                                    <div className="flex justify-between items-start mb-1">
                                        <h3 className={`font-medium ${!msg.is_read ? 'text-gray-900 font-bold' : 'text-gray-700'}`}>
                                            {msg.name}
                                        </h3>
                                        <span className="text-xs text-gray-400 whitespace-nowrap ml-2">
                                            {format(new Date(msg.created_at), 'dd MMM', { locale: fr })}
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-600 truncate">{msg.message}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Message Detail */}
            <div className={`flex-[2] bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col ${!selectedMessage ? 'hidden md:flex' : 'flex'}`}>
                {selectedMessage ? (
                    <>
                        <div className="p-6 border-b border-gray-100 flex justify-between items-start">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900 mb-1">{selectedMessage.name}</h2>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-gray-500">
                                    <a href={`mailto:${selectedMessage.email}`} className="flex items-center gap-1 hover:text-primary-600">
                                        <Mail size={14} /> {selectedMessage.email}
                                    </a>
                                    {selectedMessage.phone && (
                                        <a href={`tel:${selectedMessage.phone}`} className="flex items-center gap-1 hover:text-primary-600">
                                            <Phone size={14} /> {selectedMessage.phone}
                                        </a>
                                    )}
                                    <span className="flex items-center gap-1">
                                        <Calendar size={14} />
                                        {format(new Date(selectedMessage.created_at), 'dd MMM yyyy à HH:mm', { locale: fr })}
                                    </span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setSelectedMessage(null)}
                                    className="md:hidden p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                                >
                                    Fermer
                                </button>
                                <button
                                    onClick={() => handleMarkAsRead(selectedMessage.id, selectedMessage.is_read)}
                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${selectedMessage.is_read
                                            ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                            : 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                                        }`}
                                >
                                    {selectedMessage.is_read ? (
                                        <>
                                            <CheckCircle size={16} /> Lu
                                        </>
                                    ) : (
                                        <>
                                            <Circle size={16} /> Marquer comme lu
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                        <div className="p-6 overflow-y-auto flex-1">
                            <p className="whitespace-pre-wrap text-gray-800 leading-relaxed">
                                {selectedMessage.message}
                            </p>
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8">
                        <Mail size={64} className="mb-4 opacity-20" />
                        <p>Sélectionnez un message pour le lire.</p>
                    </div>
                )}
            </div>
        </div>
    );
}
